import { addDays, differenceInDays, isBefore } from 'date-fns';
import Decimal from 'decimal.js';
import { isReceiptEligible } from 'anchor-scout-common/types';
export { isReceiptEligible, SHARED_RECEIPT_ELIGIBLE_STATUSES } from 'anchor-scout-common/types';
import { ApprovalMethodNoticePeriodType } from '../approval-method-notice-period';
import { displayStatus as invoiceDisplayStatus, totalAmount as invoiceTotalAmount } from '../invoice-summary/selectors';
import { MoneyModel } from '../MoneyModel';
import { InvoiceStatus } from './model';
// Re-export summary-level selectors so existing callers like `invoiceModel.formattedNumber`
// keep working. All of these only read fields present on `invoiceSummary.T` and accept both
// summary and full invoices via structural subtyping.
export { accounting, customerName, displayStatus, dueDate, formattedNumber, id, isNotSynced, isPaymentMethodUnset, isPaymentMethodUnverified, isRefundFailed, isRefundRequested, isSynced, issuedOn, otherPartyCompanyName, paymentDetails, qboIntegration, rawTax, rawTotalAmount, relationshipId, hasTax, taxDisplay, totalAmount, vendorName, xeroIntegration } from '../invoice-summary/selectors';
export function subtotalDisplay(param) {
    let { totals } = param;
    return new MoneyModel(totals.subtotal).display();
}
export function subtotal(param) {
    let { totals } = param;
    return totals.subtotal;
}
export function totalDisplay(param) {
    let { totals } = param;
    return new MoneyModel(totals.withoutCredits).display();
}
export function totalWithoutCredits(param) {
    let { totals } = param;
    return totals.withoutCredits;
}
export function balanceDueDisplay(invoice) {
    return invoiceTotalAmount(invoice);
}
export function isPending(invoice) {
    return invoice.status.type === InvoiceStatus.Pending;
}
export function isPaid(invoice) {
    return invoice.status.type === InvoiceStatus.Paid;
}
export function isConfirmed(invoice) {
    return invoice.status.type === InvoiceStatus.PaymentConfirmed;
}
export function isProcessing(invoice) {
    return invoice.status.type === InvoiceStatus.ProcessingCharge;
}
export function isPaidExternally(invoice) {
    return invoice.status.type === InvoiceStatus.PaidExternally;
}
export function isVoided(invoice) {
    return invoice.status.type === InvoiceStatus.Voided;
}
export function isVoidedFromDispute(invoice) {
    return invoice.isVoidedFromDispute;
}
export function isRefunded(invoice) {
    return invoice.status.type === InvoiceStatus.Refunded;
}
export function isPartiallyRefunded(invoice) {
    return invoice.status.type === InvoiceStatus.PartiallyRefunded;
}
export function totalRefunded(invoice) {
    return invoice.amountRefunded.toFixed(2).replace(/\.00$/, '');
}
export function hasRefundFees(invoice) {
    var _invoice_payment;
    return ((_invoice_payment = invoice.payment) === null || _invoice_payment === void 0 ? void 0 : _invoice_payment.hasRefundFees()) || false;
}
export function refundedEvents(invoice) {
    var _ref;
    var _invoice_payment;
    return ((_ref = (_invoice_payment = invoice.payment) === null || _invoice_payment === void 0 ? void 0 : _invoice_payment.committedRefunds()) !== null && _ref !== void 0 ? _ref : []).map((refund)=>{
        var _refund_amounts_items;
        return {
            lines: ((_refund_amounts_items = refund.amounts.items) !== null && _refund_amounts_items !== void 0 ? _refund_amounts_items : []).map((item)=>({
                    name: item.name,
                    amountDisplay: negatedDisplay(item.displayAmount),
                    amount: negatedDollars(item.displayAmount),
                    taxDisplay: item.taxAmount > 0 ? negatedDisplay(item.taxAmount) : null,
                    tax: item.taxAmount > 0 ? negatedDollars(item.taxAmount) : null
                }))
        };
    });
}
export function hasRefundedLines(invoice) {
    return refundedEvents(invoice).some((event)=>event.lines.length > 0);
}
function negatedDollars(cents) {
    return new Decimal(cents).div(100).neg();
}
function negatedDisplay(cents) {
    return new MoneyModel(negatedDollars(cents)).display();
}
function refundedAmountForItem(invoice, item) {
    var _ref;
    var _invoice_payment;
    return ((_ref = (_invoice_payment = invoice.payment) === null || _invoice_payment === void 0 ? void 0 : _invoice_payment.committedRefunds()) !== null && _ref !== void 0 ? _ref : []).flatMap((refund)=>{
        var _refund_amounts_items;
        return (_refund_amounts_items = refund.amounts.items) !== null && _refund_amounts_items !== void 0 ? _refund_amounts_items : [];
    }).filter((refundedItem)=>refundedItem.id === item.id).reduce((total, refundedItem)=>total.add(new Decimal(refundedItem.displayAmount).div(100)), new Decimal(0));
}
export function itemRefundState(invoice, item) {
    const refunded = refundedAmountForItem(invoice, item);
    if (refunded.isZero()) return {
        type: 'none'
    };
    const itemDisplayAmount = new Decimal(itemAmounts(item).displayAmount);
    if (isRefunded(invoice) || refunded.greaterThanOrEqualTo(itemDisplayAmount)) return {
        type: 'fullyRefunded'
    };
    return {
        type: 'partiallyRefunded',
        amountDisplay: new MoneyModel(refunded).display()
    };
}
export function netPaidAmount(invoice) {
    var _ref;
    var _invoice_payment;
    return (_ref = (_invoice_payment = invoice.payment) === null || _invoice_payment === void 0 ? void 0 : _invoice_payment.remainingAmount()) !== null && _ref !== void 0 ? _ref : null;
}
export function netPaidDisplay(invoice) {
    var _invoice_payment;
    const remainingAmount = (_invoice_payment = invoice.payment) === null || _invoice_payment === void 0 ? void 0 : _invoice_payment.remainingAmount();
    return remainingAmount == null ? null : new MoneyModel(remainingAmount).display();
}
export function cardFeeFigures(invoice) {
    const payment = invoice.payment;
    if (payment == null || !isReceiptEligible(invoiceDisplayStatus(invoice), invoice.originalPaymentTimestamp)) {
        return null;
    }
    const amountCharged = payment.amountCharged();
    const netPaid = payment.netPaid();
    const closingFigure = hasRefundedLines(invoice) ? netPaid : amountCharged;
    if (closingFigure === null) return null;
    const processingFee = payment.customerProcessingFee();
    const reimbursedFees = payment.reimbursedFees();
    const shownReimbursedFees = reimbursedFees === null || reimbursedFees.isZero() ? null : reimbursedFees.neg();
    return {
        processingFeeDisplay: processingFee === null ? null : new MoneyModel(processingFee).display(),
        processingFee,
        reimbursedFeesDisplay: shownReimbursedFees === null ? null : new MoneyModel(shownReimbursedFees).display(),
        reimbursedFees: shownReimbursedFees,
        balanceDueDisplay: amountCharged === null ? balanceDueDisplay(invoice) : new MoneyModel(amountCharged).display(),
        balanceDue: amountCharged !== null && amountCharged !== void 0 ? amountCharged : invoice.rawTotalAmount,
        netPaidDisplay: netPaid === null ? netPaidDisplay(invoice) : new MoneyModel(netPaid).display(),
        netPaid: netPaid !== null && netPaid !== void 0 ? netPaid : netPaidAmount(invoice)
    };
}
export function isPaymentStopped(invoice) {
    return invoice.status.type === InvoiceStatus.PaymentStopped;
}
export function isPaymentFailed(invoice) {
    return invoice.status.type === InvoiceStatus.PaymentFailed || invoice.status.type === InvoiceStatus.PaymentLateReturned;
}
export function isRequiresPaymentApproval(invoice) {
    return invoice.status.type === InvoiceStatus.RequiresPaymentApproval;
}
export function isPaymentActionRequired(invoice) {
    return invoice.status.type === InvoiceStatus.PaymentActionRequired;
}
export function isDisputed(invoice) {
    return invoice.status.type === InvoiceStatus.PaymentDisputed;
}
export function isPaymentDisputeWon(invoice) {
    return invoice.status.type === InvoiceStatus.PaymentDisputeWon;
}
export function statusType(invoice) {
    return invoice.status.type;
}
export function disputedMessage(invoice) {
    return invoice.status.type === InvoiceStatus.PaymentDisputed ? invoice.status.paymentDisputed.message : null;
}
export function issueDate(invoice) {
    return invoice.issuedOn || invoice.createdOn;
}
// WHY: a bill-now with a past issue date processes on the createdOn date, not the due date
export function paymentProcessingDate(invoice) {
    return isBefore(invoice.rawDueDate, invoice.rawCreatedOn) ? invoice.createdOn : invoice.dueDate;
}
export function paymentFailureMessage(param) {
    let { status } = param;
    if (status.type === InvoiceStatus.PaymentFailed) return status.paymentFailed.message;
    if (status.type === InvoiceStatus.PaymentLateReturned) return status.paymentLateReturned.message;
    return undefined;
}
export function paymentFailureCode(param) {
    let { status } = param;
    if (status.type === InvoiceStatus.PaymentFailed) return status.paymentFailed.failureCode;
    if (status.type === InvoiceStatus.PaymentLateReturned) return status.paymentLateReturned.failureCode;
    return undefined;
}
export function refundFailureCode(param) {
    let { status } = param;
    if (status.type === InvoiceStatus.RefundFailed) return status.refundFailed.failureCode;
    return undefined;
}
export function processingAction(param) {
    let { status } = param;
    if (status.type === InvoiceStatus.PaymentActionRequired) {
        const { processingAction } = status.paymentActionRequired;
        return {
            stripeUrl: processingAction.url,
            clientSecret: processingAction.clientSecret
        };
    } else {
        return {
            stripeUrl: null,
            clientSecret: null
        };
    }
}
export function isStableIntegrationStatus(invoice) {
    const trueFnc = ()=>true;
    return invoice.integration.accept({
        whenPendingSync: trueFnc,
        whenInvoiceSyncFailed: trueFnc,
        whenPaymentSyncFailed: trueFnc,
        whenRefundSyncFailed: trueFnc,
        whenVoidedRecorded: trueFnc,
        whenPaymentRecorded: trueFnc,
        whenRefundRecorded: trueFnc,
        whenReturnRecorded: trueFnc,
        whenInvoiceRecorded: ()=>isPending(invoice) || isPaidExternally(invoice) || isPaymentStopped(invoice) || isPaid(invoice) && invoice.isTotalAmountZero || isRequiresPaymentApproval(invoice) || isPaymentActionRequired(invoice)
    });
}
export function paymentStopReason(invoice) {
    return invoice.status.type === InvoiceStatus.PaymentStopped ? invoice.status.paymentStopped.reason : '';
}
export const autoApprovalDate = (invoice)=>{
    const { status, createdOn } = invoice;
    const approvalMethod = status.type === InvoiceStatus.RequiresPaymentApproval ? status.requiresPaymentApproval.approvalMethod : null;
    return approvalMethod && approvalMethod.type === ApprovalMethodNoticePeriodType.Automatic ? addDays(new Date(createdOn), approvalMethod.auto.noticePeriodDays) : null;
};
export const itemName = (param)=>{
    let { name } = param;
    return name;
};
export const itemQuantity = (param)=>{
    let { quantity } = param;
    return quantity || '';
};
export const itemDiscount = (param)=>{
    let { discount } = param;
    return discount;
};
export const itemTotalAmount = (param)=>{
    let { totalAmount } = param;
    return totalAmount;
};
export const itemAmounts = (param)=>{
    let { amounts } = param;
    return amounts;
};
export const itemNote = (param)=>{
    let { note } = param;
    return note;
};
export const itemTotalAmountDecimal = (param)=>{
    let { totalAmount } = param;
    return new Decimal(totalAmount.replace(/[,$]/g, ''));
};
export const isCreditItem = (item)=>itemTotalAmountDecimal(item).isNegative();
export const chargeItems = (param)=>{
    let { items } = param;
    return items.filter((item)=>!isCreditItem(item));
};
export const creditItems = (param)=>{
    let { items } = param;
    return items.filter(isCreditItem);
};
export const subItemName = (param)=>{
    let { name } = param;
    return name;
};
export const subItemDiscount = (param)=>{
    let { discount } = param;
    return discount;
};
export const subItemUnitName = (param)=>{
    let { unitName } = param;
    return unitName;
};
export const groupedLineItems = (item)=>item.groupedLineItems;
export const isGrouped = (item)=>groupedLineItems(item).length > 0;
export const payment = (param)=>{
    let { payment } = param;
    return payment;
};
export const originalPaymentTimestamp = (invoice)=>invoice.originalPaymentTimestamp;
export const numRefunds = (invoice)=>{
    var _ref;
    var _invoice_payment_refunds, _invoice_payment;
    return (_ref = (_invoice_payment = invoice.payment) === null || _invoice_payment === void 0 ? void 0 : (_invoice_payment_refunds = _invoice_payment.refunds) === null || _invoice_payment_refunds === void 0 ? void 0 : _invoice_payment_refunds.length) !== null && _ref !== void 0 ? _ref : null;
};
// WHY: clock not injected yet — every caller is the refund-action config outside ANCR-5324's scope
export const daysSincePayment = (invoice)=>{
    if (invoice.status.type === InvoiceStatus.Paid) {
        const paidTimestamp = invoice.status.paid.paymentDate;
        return differenceInDays(new Date().setHours(0, 0, 0, 0), new Date(paidTimestamp).setHours(0, 0, 0, 0));
    } else {
        return null;
    }
};
